// ChatGPT风格主题配置
export const chatGPTTheme = {
  colors: {
    // 主色调 - 类似ChatGPT的深色/浅色模式
    primary: '#10a37f',
    primaryHover: '#0d8b6f',
    primaryLight: '#1a73e8',
    secondary: '#6c7293',
    
    // 背景色 - 支持CSS变量
    background: 'var(--bg-primary, #ffffff)',
    backgroundDark: 'var(--bg-primary-dark, #343541)',
    backgroundSecondary: 'var(--bg-secondary, #f7f7f8)',
    backgroundSecondaryDark: 'var(--bg-secondary-dark, #444654)',
    backgroundGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    
    // 文本颜色 - 支持CSS变量
    text: 'var(--text-primary, #2d3748)',
    textDark: 'var(--text-primary-dark, #ffffff)',
    textSecondary: 'var(--text-secondary, #8e8ea0)',
    textMuted: 'var(--text-muted, #9ca3af)',
    
    // 边框和分割线 - 支持CSS变量
    border: 'var(--border-color, #e5e7eb)',
    borderDark: 'var(--border-color-dark, #4d4d4f)',
    
    // 悬停状态
    hover: 'var(--hover-bg, #f3f4f6)',
    hoverDark: 'var(--hover-bg-dark, #4b5563)',
    
    // 消息气泡 - 支持CSS变量
    userMessage: 'var(--user-message-bg, #10a37f)',
    assistantMessage: 'var(--assistant-message-bg, #f7f7f8)',
    assistantMessageDark: 'var(--assistant-message-bg-dark, #444654)',
    
    // 状态色
    success: '#22c55e',
    warning: '#f59e0b',
    error: '#ef4444',
    info: '#3b82f6',
    
    // 日本市场特定色彩
    paypay: '#ff6900',
    konbini: '#4a90e2',
    
    // 新增：法律相关色彩
    legal: {
      primary: '#1e40af',
      secondary: '#7c3aed',
      accent: '#dc2626',
      success: '#059669',
    },
  },
  
  // 字体设置
  fonts: {
    body: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Hiragino Sans", "Yu Gothic UI", sans-serif',
    heading: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Hiragino Sans", "Yu Gothic UI", sans-serif',
    mono: 'ui-monospace, SFMono-Regular, "SF Mono", Monaco, Consolas, monospace',
  },
  
  // 间距系统 (类似ChatGPT)
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px',
  },
  
  // 圆角设置
  borderRadius: {
    sm: '6px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    full: '50%',
  },
  
  // 阴影设置 (ChatGPT风格)
  shadows: {
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
    glow: '0 0 20px rgba(16, 163, 127, 0.3)',
  },
  
  // 断点设置
  breakpoints: {
    mobile: '640px',
    tablet: '768px',
    desktop: '1024px',
    wide: '1280px',
  },
  
  // 新增：动画配置
  animations: {
    transition: {
      fast: '0.15s ease-in-out',
      normal: '0.3s ease-in-out',
      slow: '0.5s ease-in-out',
    },
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    },
  },
  
  // 新增：性能优化配置
  performance: {
    virtualScrollThreshold: 100,
    lazyLoadOffset: 100,
    debounceDelay: 300,
  },
};

// 动态主题配置函数
export const getDynamicAntdThemeConfig = (isDark: boolean) => ({
  token: {
    colorPrimary: chatGPTTheme.colors.primary,
    fontFamily: chatGPTTheme.fonts.body,
    borderRadius: 8,
    colorBgContainer: isDark ? '#343541' : '#ffffff',
    colorText: isDark ? '#ffffff' : '#2d3748',
    colorBorder: isDark ? '#4d4d4f' : '#e5e7eb',
    colorBgLayout: isDark ? '#343541' : '#ffffff',
    colorBgElevated: isDark ? '#444654' : '#ffffff',
    colorTextSecondary: isDark ? '#d1d5db' : '#8e8ea0',
    motionDurationFast: '0.1s',
    motionDurationMid: '0.2s',
    motionDurationSlow: '0.3s',
  },
  components: {
    Layout: {
      bodyBg: isDark ? '#343541' : '#ffffff',
      headerBg: isDark ? '#343541' : '#ffffff',
      siderBg: isDark ? '#444654' : '#f7f7f8',
    },
    Input: {
      colorBgContainer: isDark ? '#444654' : '#ffffff',
      colorText: isDark ? '#ffffff' : '#2d3748',
      colorBorder: isDark ? '#4d4d4f' : '#e5e7eb',
    },
    Button: {
      colorBgContainer: isDark ? '#565869' : '#f7f7f8',
      colorText: isDark ? '#ffffff' : '#2d3748',
      colorBorder: isDark ? '#4d4d4f' : '#e5e7eb',
    },
    Card: {
      colorBgContainer: isDark ? '#444654' : '#ffffff',
      colorText: isDark ? '#ffffff' : '#2d3748',
      colorBorder: isDark ? '#4d4d4f' : '#e5e7eb',
    },
    Menu: {
      colorBgContainer: isDark ? '#444654' : '#f7f7f8',
      colorText: isDark ? '#ffffff' : '#2d3748',
      colorItemBg: 'transparent',
      colorItemTextHover: isDark ? '#ffffff' : '#2d3748',
      colorItemBgHover: isDark ? '#6b7280' : '#e5e7eb',
    },
  },
});

// 保持兼容性的默认配置
export const antdThemeConfig = getDynamicAntdThemeConfig(false);

export type Theme = typeof chatGPTTheme; 